//-- Copyright (c) 2021 TMT Insights Group, LLC
import {
  DateTimeDisplay,
  JobTypeLabel,
  JobRequirements,
  TableJobStage,
  JobsOverflowMenu,
  UserDisplay,
  JobsEmbargoDate,
  Notes,
  ClickCopy
} from '@tmt-insights/oms_fe_core/columns';
import { StatusColors } from '@tmt-insights/oms_fe_core/constants';
import { Grid } from '@tmt-insights/oms_fe_core/material';
import { AllTableColumnsConfig } from '@/__configs/config_types';
import { JobDetail, TitleEntity } from '@/redux/slices/slice_types';
import { findandFormatUTCDateInString } from '@tmt-insights/oms_fe_core/date';
import TitleHierarchy from '../../components/TitleHierarchy';

const columns: AllTableColumnsConfig<JobDetail> = {
  hid: {
    label: 'Job ID',
    sortField: 'id',
    render: (cell: JobDetail['hid']) => (
      <ClickCopy copyText={cell} noBackground disableTextClick>
        <strong style={{ color: 'var(--primary-main)' }}>{cell}</strong>
      </ClickCopy>
    )
  },
  job_type: {
    label: 'Job Type',
    render: (cell: JobDetail['job_type']) => <JobTypeLabel type={cell} />
  },
  title: {
    label: 'Title',
    clickCopy: true,
    minWidth: 250,
    render: (cell: TitleEntity, row: JobDetail) => <TitleHierarchy title={cell} versionName={row.version?.name} />
  },
  version__program_id: {
    label: 'Program ID'
  },
  version__piece_id: {
    label: 'Piece ID'
  },
  version__piece_number: {
    label: 'Piece Number'
  },
  output_version__meta__network_name: {
    label: 'Network',
    render: (_cell: any, row: JobDetail) => {
      const [code] = Object.values(row.output_network ?? {});

      return code ?? '-';
    }
  },
  output_title__external_id: {
    defaultDisabled: true,
    label: 'Output Program ID'
  },
  output_version__external_id: {
    defaultDisabled: true,
    label: 'Output Version ID',
    tooltip: {
      title: 'Displays Core Version ID or Piece ID',
      placement: 'top'
    }
  },
  output_edit_type: {
    defaultDisabled: true,
    label: 'Output Master Type',
    sortField: 'validated_workflow_inputs__edit_type'
  },
  source_asset_type: {
    label: 'Source Asset Type'
  },
  order__managed_by: {
    label: 'Managed By',
    disableSort: true,
    minWidth: 100,
    render: (cell: JobDetail['order']['managed_by'], row: JobDetail) => (cell ? <UserDisplay user={cell} emptyText="-" /> : row.managed_by || '-')
  },
  customer: {
    label: 'Customer',
    clickCopy: true,
    minWidth: 180
  },
  profile: {
    label: 'Profile',
    clickCopy: true,
    sortField: 'job_profile',
    minWidth: 200,
    render: (cell: JobDetail['profile']) => findandFormatUTCDateInString(cell?.name)
  },
  due_date: {
    label: 'Due Date',
    clickCopy: true,
    minWidth: 100,
    render: (cell: JobDetail['due_date']) => <DateTimeDisplay>{cell}</DateTimeDisplay>
  },
  created_at: {
    label: 'Created',
    clickCopy: true,
    minWidth: 100,
    render: (cell: JobDetail['created_at']) => <DateTimeDisplay>{cell}</DateTimeDisplay>
  },
  completed_at: {
    defaultDisabled: true,
    label: 'Completed',
    minWidth: 100,
    render: (cell: JobDetail['completed_at']) => <DateTimeDisplay>{cell}</DateTimeDisplay>
  },
  stage: {
    label: 'Status/Stage',
    render: (cell: JobDetail['stage'], row: JobDetail) => (
      <Grid container rowSpacing={1}>
        <Grid item xs={12}>
          <TableJobStage status={row.status === 'cancelled' ? row.status : cell || row.status} colorOverride={StatusColors[row.status]} />
        </Grid>
        {row?.embargo_date && !row?.embargo_resumed_at && (
          <Grid item xs={12}>
            <JobsEmbargoDate job={row} />
          </Grid>
        )}
      </Grid>
    )
  },
  assets: {
    label: 'Requirements',
    render: (cell: JobDetail['assets'], row: JobDetail) => (
      <div style={{ opacity: row.status === 'cancelled' ? '0.5' : '1', marginLeft: -15 }}>
        <JobRequirements sources={cell} />
      </div>
    ),
    tooltip: {
      title: 'Displays sources received out of the total expected',
      placement: 'top'
    }
  },
  notes: {
    label: 'Notes',
    render: (_cell: any, row: JobDetail) => (
      <div style={{ width: 40, textAlign: 'center' }}>
        <Notes data={row} type="jobs" />
      </div>
    ),
    disableSort: true
  },
  overflow: {
    label: '',
    isOverflow: true,
    render: (_cell: any, row: JobDetail) => <JobsOverflowMenu row={row} />
  }
};

export default columns;
